import cn from "classnames";
import { useEffect, useMemo, useState } from "react";
import { type ConnectedProps, connect } from "react-redux";
import { type Dispatch, bindActionCreators } from "redux";
import { Icon, Table } from "semantic-ui-react";

import CardReporting, { type CardReportingItem } from "../../../../../components/cards/CardReporting/CardReporting";
import { HorizontalBarWrapper, TickValueType } from "../../../../../components/charts";
import * as assessmentPerformanceActions from "../../state/thunks/assessmentPerformanceThunk";

import { type DataPoint } from "../../../../../components/charts/types/HorizontalBarChart";
import { RequestStatusRenderer } from "../../../../../components/requestStatsRenderer/RequestStatusRenderer";
import { type RootState } from "../../../../Application/globaltypes/redux";
import {
  resetAnswerData,
  selectQuestionResponsePeople,
  selectQuestionResponses,
  selectQuestionResponsesData,
  selectQuestionResponsesValue,
} from "../../state/slices/assessmentPerformanceSlice";

import { ListViewBase } from "../../../../../components/listView";
import { PerformanceTabSectionList } from "../../../Common/Performance/PerformanceTabSectionList/PerformanceTabSectionList";

import { type ColumnOption } from "../../../../../interfaces/columnOptions";
import {
  type AssessmentPersonResponse,
  type AssessmentQuestionAnswer,
  type EngagementQuestionsData,
} from "../../types/state";

import { FeatureFlags } from "featureFlags";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import dataService from "../../services/assessmentsDataService";

import { ReportUnavailable } from "components/reportUnavailable/ReportUnavailable";
import { RStatus } from "features/Application/globaltypes/fetchRequest";
import {
  type PerformanceFilter,
  fallbackToZero,
  horizontalBarChartMargins,
  isUserDeleted,
  formatUserName,
  canUseChildAccount,
  isUserFromCurrentAccount as isUserFromCurrentAccountUtil,
  getLinkNavigationProps,
} from "features/Library/Common/utils/performanceUtils";
import { Tooltip } from "components/common/tooltip";
import { TextTruncate } from "components";
import { bindAction } from "interfaces";
import { setHasDataToExport } from "features/Reporting/state/export/exportSlice";
import { RouteNames } from "enums";

import "./AssessmentPerformanceDrillDown.scss";
import { MoboLink } from "components/MoboLink/MoboLink";
import { roundToTwoDigits } from "features/Library/Common/utils/roundToTwoDigits";
import { useReportingFormatWithTimeZone } from "hooks/useReportingFormatWithTimeZone";

export interface Props extends PropsFromRedux {
  selectedQuestionIndex: number;
  selectedQuestion: EngagementQuestionsData;
  selectedAnswer?: AssessmentQuestionAnswer;
  onSelectAnswer: (answer: AssessmentQuestionAnswer | undefined) => void;
  totalQuestionCount: number;
  assessmentId: number;
  flowId?: number;
  dateFilter: PerformanceFilter;
  hideHeader?: boolean;
  onNextQuestion: () => void;
  onPrevQuestion: () => void;
  includeAccounts?: boolean;
  childAccountId?: number;
  hasCreateAccountsAddOn?: boolean;
}

export const AssessmentPerformanceDrillDown: React.FC<Props> = ({
  selectedQuestionIndex,
  selectedQuestion,
  selectedAnswer,
  onSelectAnswer,
  totalQuestionCount,
  assessmentId,
  flowId,
  dateFilter,
  hideHeader,
  questionResponsesStatus,
  questionResponsesData,
  questionResponsesPeopleState,
  questionResponsesPeopleList,
  actions,
  resetAnswerData,
  onNextQuestion,
  onPrevQuestion,
  setHasExportData,
  includeAccounts,
  accountId,
  childAccountId,
  hasCreateAccountsAddOn,
}) => {
  const [slicedEntities, setSliceEntities] = useState<AssessmentPersonResponse[]>([]);
  const formatDateTime = useReportingFormatWithTimeZone();

  const reportEnabled = useFeatureFlag(FeatureFlags.AssessmentReport);

  useEffect(() => {
    setHasExportData({
      hasDataToExport: questionResponsesPeopleState.status === RStatus.Got && !!questionResponsesPeopleList?.length,
      tooltipContent: "No data available for export. A question response must be selected.",
    });
  }, [setHasExportData, questionResponsesPeopleState.status, questionResponsesPeopleList]);

  useEffect(() => {
    if (selectedQuestion && reportEnabled) {
      actions.fetchQuestionResponsesData(assessmentId, selectedQuestion.QuestionId, { ...dateFilter, flowId });
    }
  }, [actions, assessmentId, dateFilter, flowId, selectedQuestion, reportEnabled]);

  useEffect(() => {
    // This should be removed once issue with answer click on v2
    // resolved
    if (selectedAnswer && reportEnabled) {
      dataService.cancelAssessmentQuestionsPeople();
      // Only fetch if there is data
      if (selectedAnswer.ChoicePercent && selectedAnswer.ChoicePercent > 0) {
        actions.fetchEngagementQuestionsPeopleData(assessmentId, selectedQuestion.QuestionId, selectedAnswer.AnswerId, {
          ...dateFilter,
          flowId,
        });
      }
      // Clear fetched answer data if we're changing to an answer with no data
      else {
        resetAnswerData();
      }
    }
  }, [
    actions,
    assessmentId,
    selectedQuestion.QuestionId,
    selectedAnswer,
    resetAnswerData,
    dateFilter,
    flowId,
    reportEnabled,
  ]);

  const formattedResponses = useMemo(() => {
    if (questionResponsesStatus.status !== RStatus.Got) return [];

    if (!questionResponsesData) return [];

    return questionResponsesData.Answers.map<DataPoint>((answer) => {
      const calculatedValue = Math.round(
        ((questionResponsesData.CorrectAttempts ?? 0) + (questionResponsesData.IncorrectAttempts ?? 0)) *
          (answer.ChoicePercent ?? 0),
      );

      if (answer.IsAnswerCorrect) {
        return {
          id: answer.AnswerId,
          category: answer.AnswerTitle,
          value: calculatedValue,
          fillColor: "#62cd6e",
          iconClass: "green check circle",
        };
      } else {
        return {
          id: answer.AnswerId,
          category: answer.AnswerTitle,
          value: calculatedValue,
        };
      }
    });
  }, [questionResponsesData, questionResponsesStatus.status]);

  const columns: ColumnOption<AssessmentPersonResponse>[] = [
    {
      name: "Name",
      width: 7,
      isSortable: false,
      render(user) {
        const formattedNameDisplay = formatUserName(user.FirstName, user.LastName);
        const childAccountEnabled = canUseChildAccount(childAccountId, hasCreateAccountsAddOn);
        const shouldShowLink = !isUserDeleted(user) && (!includeAccounts || childAccountEnabled || !childAccountId);
        const userName = (
          <Tooltip content={formattedNameDisplay} target={<TextTruncate>{formattedNameDisplay}</TextTruncate>} />
        );
        const isUserFromCurrentAccount = isUserFromCurrentAccountUtil(
          user.AccountId,
          accountId,
          childAccountId,
          hasCreateAccountsAddOn,
        );
        const { accountId: linkAccountId, target: linkTarget } = getLinkNavigationProps({
          childAccountEnabled,
          isUserFromCurrentAccount,
          childAccountId,
          fallbackAccountId: user.AccountId,
        });
        return (
          <div>
            {shouldShowLink ? (
              <MoboLink
                className="linkButton"
                to={`/${RouteNames.peopleUsers}/${user.UserId}`}
                accountId={linkAccountId}
                target={linkTarget}
              >
                {userName}
              </MoboLink>
            ) : (
              userName
            )}
            <div className="userDepartmentTitle">
              {user.Department && (
                <Tooltip content={user.Department} target={<TextTruncate>{user.Department}</TextTruncate>} />
              )}
            </div>
          </div>
        );
      },
    },
    {
      name: "Email",
      width: 7,
      isSortable: false,
      render(user) {
        return <Tooltip target={<TextTruncate>{user.Email}</TextTruncate>} content={`${user.Email}`} />;
      },
    },
    {
      name: "Date",
      width: 7,
      isSortable: false,
      render(user) {
        return formatDateTime(user.Date);
      },
    },
    {
      name: "Account",
      width: 7,
      isSortable: false,
      render(user) {
        return user.AccountName ?? "-";
      },
    },
  ];

  const buildTableBody = (item: AssessmentPersonResponse) => {
    return columns.map((column, index) => (
      <Table.Cell width={column.width} key={`${column.name}_${index}`}>
        {column.render(item)}
      </Table.Cell>
    ));
  };

  const selectAnswer = (index: number | null) => {
    if (index !== null && index !== -1) {
      const answerId = formattedResponses[index].id;
      const toIndex = selectedQuestion.Answers.findIndex((p) => p.AnswerId === answerId);
      if (toIndex === -1) {
        // This should never happen, if the selectedQuestion and another
        // API request are not in sync
        return;
      }
      onSelectAnswer(selectedQuestion.Answers[toIndex]);
    }
  };

  const getData = (skip: number, top: number) => {
    setSliceEntities(questionResponsesPeopleList.slice(skip, skip + top));
  };

  if (!reportEnabled) {
    return <ReportUnavailable />;
  }

  return (
    <div className="drillDownRoot">
      {!hideHeader && (
        <div className="navigation">
          <div className="questionTitle">{selectedQuestion.QuestionText}</div>
          <div className="assessmentActions">
            <span className="questionCount">
              {selectedQuestionIndex + 1} / {totalQuestionCount} questions
            </span>
            <button
              title="Previous question"
              aria-label="Previous question"
              data-testid="previousQuestion"
              className={cn("invisibleButton", "leftSpacing")}
              onClick={onPrevQuestion}
            >
              <Icon className="chevron left" />
            </button>
            <button
              title="Next question"
              aria-label="Next question"
              data-testid="nextQuestion"
              className={cn("invisibleButton", "leftSpacing")}
              onClick={onNextQuestion}
            >
              <Icon className="chevron right" />
            </button>
          </div>
        </div>
      )}
      <div className="cardSection">
        <RequestStatusRenderer state={questionResponsesStatus}>
          <CardReporting
            items={
              [
                {
                  statDescription: "Incorrect Attempts",
                  stat: fallbackToZero(questionResponsesData.IncorrectAttempts),
                  popupHeader: "Incorrect Attempts",
                  popupBody: "Total number of times the question has been answered incorrectly by users.",
                  width: "18rem",
                },
                {
                  statDescription: "Correct Answers",
                  stat: fallbackToZero(questionResponsesData.CorrectAttempts),
                  popupHeader: "Correct Answers",
                  popupBody: "Total number of times the question has been answered correctly by users.",
                  width: "18rem",
                },
                {
                  statDescription: "Avg Attempts",
                  stat: fallbackToZero(questionResponsesData.AvgAttempts),
                  popupHeader: "Average Attempts",
                  popupBody: "Average number of question attempts per user",
                  width: "13.1rem",
                },
                {
                  statDescription: "Completion Rate",
                  stat: fallbackToZero(questionResponsesData.CompletionRate) * 100,
                  isStatPercentage: true,
                  popupHeader: "Completion Rate",
                  popupBody: "Percentage of users who have completed the question",
                  width: "7.1rem",
                },
              ] as CardReportingItem[]
            }
          />
        </RequestStatusRenderer>
      </div>
      <div className="barChart">
        <RequestStatusRenderer state={questionResponsesStatus}>
          <HorizontalBarWrapper
            title="Responses"
            margins={horizontalBarChartMargins}
            selectedValue={selectedAnswer?.AnswerId}
            onChange={selectAnswer}
            data={formattedResponses}
            countBarsToShowAtOnce={7}
            tooltipFormatter={(d) => {
              const foundAnswer = questionResponsesData.Answers.find((a) => a.AnswerId === d.id);
              const displayValue = isNaN(d.value) ? "0" : roundToTwoDigits(d.value);
              if (!foundAnswer) return displayValue;
              return `${displayValue} (${foundAnswer.ChoicePercent ? Math.round((foundAnswer as any).ChoicePercent * 100) : 0}%)`;
            }}
            xTickValueType={TickValueType.IntegersOnly}
          />
        </RequestStatusRenderer>
      </div>
      <div className="answer-details-container">
        <RequestStatusRenderer state={questionResponsesPeopleState}>
          {questionResponsesPeopleList && (
            <PerformanceTabSectionList
              title="People"
              count={questionResponsesPeopleList.length}
              renderList={(page) => (
                <ListViewBase
                  columnOptions={columns}
                  items={slicedEntities}
                  loadPage={getData}
                  itemsAmount={questionResponsesPeopleList.length}
                  buildTableBody={buildTableBody}
                  withFooter={false}
                  activePage={page}
                  withSelection={false}
                />
              )}
            />
          )}
        </RequestStatusRenderer>
      </div>
    </div>
  );
};

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => {
  return {
    questionResponsesStatus: selectQuestionResponsesData(state),
    questionResponsesData: selectQuestionResponsesValue(state),
    questionResponsesPeopleState: selectQuestionResponsePeople(state),
    questionResponsesPeopleList: selectQuestionResponses(state),
    accountId: state.userProfile.accountId,
  };
};

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: Dispatch) => {
  return {
    actions: bindActionCreators(assessmentPerformanceActions, dispatch),
    resetAnswerData: bindActionCreators(resetAnswerData, dispatch),
    setHasExportData: bindAction(setHasDataToExport, dispatch),
  };
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(AssessmentPerformanceDrillDown);
export default ConnectedComponent;
